import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@clarix/ui';
import {
  normalizeError,
  useGrantConsent,
  usePublicConsent,
  useRevokeConsent,
  type PublicConsentPurpose,
} from '@/core/api';

/**
 * PUBLIC consent self-service page — DPDP §6(4) ("withdrawal shall be as easy
 * as giving consent"). Reached from the "Manage or withdraw consent" button in
 * a consent-notice email, at `/consent/manage?token=…`.
 *
 * WHY IT LIVES HERE, AT THE APEX, OUTSIDE EVERY GUARD
 * ---------------------------------------------------
 * The visitor is a DATA PRINCIPAL — an external contact with no Clarix account.
 * They cannot pass RequireAuth, they have no workspace for WorkspaceShell to
 * resolve, and they hold no CRM app grant for RequireApp. The emailed link used
 * to point at `/consent/revoke`, which matched no route at all: the shell's
 * catch-all sits inside RequireAuth, so the click ended at /login — a page
 * asking a stranger to sign in to an account they do not have.
 *
 * So this is a sibling of `/invites/:token`: apex, public, self-contained, and
 * deliberately NOT wrapped in AuthShell — that layout's "Sign in to access your
 * workspace" framing is exactly the wrong message for this audience.
 *
 * The `?token=` is the whole authorisation. The page treats it as opaque and
 * never decodes it; crm-api verifies signature, audience and workspace, and
 * decides which purposes this link may act on.
 */
export function ConsentManagePage() {
  const [params] = useSearchParams();
  const token = params.get('token');

  const consent = usePublicConsent(token);
  const revoke = useRevokeConsent(token);
  const grant = useGrantConsent(token);

  // Which purpose row is mid-request — the page has one control per purpose, so
  // a single `isPending` would spin all of them.
  const [pendingPurpose, setPendingPurpose] = useState<string | null>(null);

  // No token at all — including every legacy `/consent/revoke?contact_id=…`
  // link still sitting in an inbox. Those can never work (the id-based shape was
  // replaced precisely because it authorised nobody).
  if (!token) {
    return (
      <LinkProblem
        title="This link is incomplete"
        lead="The address you followed is missing the secure code that identifies your consent record."
      />
    );
  }

  if (consent.isLoading) {
    return (
      <Shell>
        <p className="text-lg text-slate-500">Loading your consent details…</p>
      </Shell>
    );
  }

  if (consent.isError || !consent.data) {
    // NEVER surface the raw error to a data principal. A transport failure
    // ("Cannot read properties of undefined", "Network Error") is meaningless to
    // them and leaks internals to a page anyone on the internet can open. Only
    // the backend's one deliberate, human-written CONSENT_LINK_INVALID message
    // is passed through; everything else becomes a generic retry prompt.
    const err = consent.error ? normalizeError(consent.error) : null;
    const isKnownInvalid = err?.code === 'CONSENT_LINK_INVALID';

    return isKnownInvalid ? (
      <LinkProblem
        title="This link is no longer valid"
        lead="We could not open your consent record from this link."
      />
    ) : (
      <Shell>
        <h1 className="text-3xl font-semibold text-slate-900 dark:text-slate-100">
          Something went wrong
        </h1>
        <p className="mt-3 text-lg text-slate-600 dark:text-slate-400">
          We could not load your consent details just now. This is a problem on our side, not with
          your link.
        </p>
        <Button type="button" className="mt-6 w-full" onClick={() => void consent.refetch()}>
          Try again
        </Button>
      </Shell>
    );
  }

  const view = consent.data;
  const actionError = revoke.error ?? grant.error;

  const act = (purpose: PublicConsentPurpose) => {
    setPendingPurpose(purpose.purpose_code);
    const mutation = purpose.is_active ? revoke : grant;
    mutation.mutate(purpose.purpose_code, { onSettled: () => setPendingPurpose(null) });
  };

  const activeCount = view.purposes.filter((p) => p.is_active).length;

  return (
    <Shell>
      <header>
        <p className="text-lg font-medium text-indigo-600 dark:text-indigo-400">
          {view.controller_name}
        </p>
        <h1 className="mt-1 text-3xl font-semibold text-slate-900 dark:text-slate-100">
          {view.subject_name ? `Your consent, ${view.subject_name}` : 'Your consent'}
        </h1>
        <p className="mt-2 text-lg text-slate-600 dark:text-slate-400">
          Under India&rsquo;s Digital Personal Data Protection Act, 2023, you can withdraw your
          consent at any time. Withdrawing is as easy as giving it, and you can withdraw each
          purpose on its own.
        </p>
      </header>

      <p className="mt-6 text-base text-slate-500">
        {activeCount} of {view.purposes.length}{' '}
        {view.purposes.length === 1 ? 'purpose is' : 'purposes are'} currently active.
      </p>

      {actionError && (
        <p className="mt-4 rounded-md bg-red-50 px-3 py-2 text-lg text-red-700 dark:bg-red-950/40 dark:text-red-300">
          {normalizeError(actionError).code === 'CONSENT_LINK_INVALID'
            ? normalizeError(actionError).message
            : 'We could not save that change. Please try again in a moment.'}
        </p>
      )}

      {/* One card per purpose — the notice covers several, and DPDP treats each
          as a separate consent, so each needs its own control. */}
      <ul className="mt-4 space-y-3">
        {view.purposes.map((purpose) => (
          <li
            key={purpose.purpose_code}
            className="rounded-xl border border-[rgb(var(--border))] p-4"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="font-medium text-slate-900 dark:text-slate-100">
                  {purpose.purpose_label}
                </p>
                <p className="mt-1 text-base text-slate-600 dark:text-slate-400">
                  {purpose.purpose_description}
                </p>
              </div>
              <StatusPill isActive={purpose.is_active} />
            </div>

            <div className="mt-3 flex items-center justify-between gap-4">
              <p className="text-base text-slate-400">
                {purpose.is_active
                  ? purpose.granted_at
                    ? `Given ${formatDate(purpose.granted_at)}`
                    : ''
                  : purpose.revoked_at
                    ? `Withdrawn ${formatDate(purpose.revoked_at)}`
                    : 'Not currently given'}
              </p>
              <Button
                type="button"
                size="sm"
                variant={purpose.is_active ? 'destructive' : 'outline'}
                disabled={pendingPurpose !== null}
                onClick={() => act(purpose)}
              >
                {pendingPurpose === purpose.purpose_code
                  ? 'Saving…'
                  : purpose.is_active
                    ? 'Withdraw'
                    : 'Give again'}
              </Button>
            </div>
          </li>
        ))}
      </ul>

      <div className="mt-6 rounded-xl bg-slate-50 p-4 dark:bg-slate-900/50">
        <p className="text-base font-medium text-slate-700 dark:text-slate-300">Retention period</p>
        <p className="mt-1 text-base text-slate-600 dark:text-slate-400">
          {/* The ENFORCED period — the retention policy configured for this record
              type — not the notice template's default. The two used to disagree,
              so the notice quoted a number the product did not honour. */}
          Your data is kept for up to{' '}
          <span className="font-medium">{view.retention_days} days</span> from collection, after
          which it is anonymised or deleted unless a separate legal obligation requires longer.
        </p>
      </div>

      {view.support_email && (
        <p className="mt-6 border-t border-slate-200 pt-4 text-center text-base text-slate-500 dark:border-slate-800">
          Questions or a grievance? Contact the Data Protection Officer at{' '}
          <a
            href={`mailto:${view.support_email}`}
            className="text-indigo-600 hover:underline dark:text-indigo-400"
          >
            {view.support_email}
          </a>
          .
        </p>
      )}
    </Shell>
  );
}

/**
 * The unusable-link state.
 *
 * This page is the ONLY thing a data principal has: there is no account to sign
 * into and no support desk inside the product, so a dead end here ends their
 * ability to exercise a statutory right. It therefore explains the likely
 * causes in plain language and always leaves a route forward.
 *
 * It deliberately cannot name the organisation or its DPO address — resolving
 * either requires a valid token, and a page that revealed them from an
 * unverified one would leak tenant data to anyone guessing URLs.
 */
function LinkProblem({ title, lead }: { title: string; lead: string }) {
  return (
    <Shell>
      <h1 className="text-3xl font-semibold text-slate-900 dark:text-slate-100">{title}</h1>
      <p className="mt-3 text-lg text-slate-600 dark:text-slate-400">{lead}</p>

      <p className="mt-6 text-base font-medium text-slate-700 dark:text-slate-300">
        This usually means one of:
      </p>
      <ul className="mt-2 list-disc space-y-1.5 pl-5 text-base text-slate-600 dark:text-slate-400">
        <li>
          The link was split across lines by your email app — try opening the notice again and
          clicking the button rather than copying the address.
        </li>
        <li>The link has expired. Consent links stay valid for one year.</li>
        <li>You have an older notice open; a newer one will have a working link.</li>
        <li>The record has since been deleted, in which case there is nothing left to withdraw.</li>
      </ul>

      <div className="mt-6 rounded-xl bg-slate-50 p-4 dark:bg-slate-900/50">
        <p className="text-base font-medium text-slate-700 dark:text-slate-300">
          You can still exercise your rights
        </p>
        <p className="mt-1 text-base text-slate-600 dark:text-slate-400">
          Reply to the consent notice you received. Ask to withdraw consent, or to access, correct
          or erase your data — the organisation must action it. Under the DPDP Act you may also
          raise a grievance with their Data Protection Officer, whose address is at the bottom of
          every notice they send you.
        </p>
      </div>
    </Shell>
  );
}

function StatusPill({ isActive }: { isActive: boolean }) {
  return isActive ? (
    <span className="shrink-0 rounded-full bg-emerald-50 px-2.5 py-0.5 text-base font-medium text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
      Active
    </span>
  ) : (
    <span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-0.5 text-base font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
      Withdrawn
    </span>
  );
}

/**
 * Matches the email's `{{formatDate}}` output (packages/emails/src/render.ts) so
 * the notice and this page never quote the same instant two different ways.
 * Fixed to Asia/Kolkata for the same reason the email is: the notice is written
 * under Indian law, and a date with no stated zone is ambiguous.
 */
function formatDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'Asia/Kolkata',
  }).format(date);
}

/**
 * Standalone page chrome. No TopBar, no SidebarRail, no auth framing — the
 * visitor is not a member of anything.
 */
function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-[rgb(var(--background))] p-4">
      <div className="w-full max-w-xl rounded-2xl border border-[rgb(var(--border))] bg-[rgb(var(--surface))] p-8 shadow-xl">
        {children}
      </div>
    </div>
  );
}
